import { useParams, useNavigate } from 'react-router-dom'
import { useMutation } from 'convex/react'
import { api } from '../../../convex/_generated/api'
import { Button } from '@/components/ui/button'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  SearchableSelect,
  type SearchableSelectOption,
} from '@/components/ui/select'
import { Label } from '@/components/ui/label'
import {
  Plus,
  Settings,
  MoreHorizontal,
  Database,
  Calendar,
  Users,
  MapPin,
  Bot,
} from 'lucide-react'
import { AgentChatPanel } from '@/components/agent/AgentChatPanel'
import {
  useProject,
  useScenarios,
  useCreateScenario,
  useDeleteScenario,
  useProjectStats,
} from '@/hooks/useVRPData'
import { LoadingSpinner } from '@/components/ui/loading-spinner'
import { SkeletonStat, SkeletonCard } from '@/components/ui/loading-states'
import { useState, useEffect } from 'react'
import { toast } from 'sonner'
import { formatDistanceToNow } from 'date-fns'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import type { Id } from '../../../convex/_generated/dataModel'
import useResponsive from '@/hooks/useResponsive'
import { cn } from '@/lib/utils'

// Enhanced timezone options with search functionality
const TIMEZONE_OPTIONS: SearchableSelectOption[] = [
  {
    value: 'Africa/Johannesburg',
    label: 'South Africa (Johannesburg)',
    keywords: ['south africa', 'johannesburg', 'sast', 'cst', 'cat', 'utc+2'],
  },
  {
    value: 'UTC',
    label: 'UTC (Coordinated Universal Time)',
    keywords: [
      'utc',
      'gmt',
      'universal',
      'greenwich',
      'zulu',
      'coordinated',
      'utc+0',
    ],
  },
  {
    value: 'Europe/London',
    label: 'United Kingdom (London)',
    keywords: [
      'uk',
      'london',
      'britain',
      'england',
      'gmt',
      'bst',
      'utc+0',
      'utc+1',
    ],
  },
  {
    value: 'Europe/Paris',
    label: 'France (Paris)',
    keywords: ['france', 'paris', 'cet', 'cest', 'utc+1', 'utc+2'],
  },
  {
    value: 'Europe/Berlin',
    label: 'Germany (Berlin)',
    keywords: ['germany', 'berlin', 'cet', 'cest', 'utc+1', 'utc+2'],
  },
  {
    value: 'Europe/Madrid',
    label: 'Spain (Madrid)',
    keywords: ['spain', 'madrid', 'cet', 'cest', 'utc+1', 'utc+2'],
  },
  {
    value: 'Europe/Amsterdam',
    label: 'Netherlands (Amsterdam)',
    keywords: [
      'netherlands',
      'amsterdam',
      'holland',
      'cet',
      'cest',
      'utc+1',
      'utc+2',
    ],
  },
  {
    value: 'Europe/Moscow',
    label: 'Russia (Moscow)',
    keywords: ['russia', 'moscow', 'msk', 'utc+3'],
  },
  {
    value: 'America/New_York',
    label: 'US Eastern (New York)',
    keywords: [
      'eastern',
      'est',
      'edt',
      'new york',
      'usa',
      'us east coast',
      'utc-5',
      'utc-4',
    ],
  },
  {
    value: 'America/Chicago',
    label: 'US Central (Chicago)',
    keywords: [
      'central',
      'cst',
      'cdt',
      'chicago',
      'usa',
      'midwest',
      'utc-6',
      'utc-5',
    ],
  },
  {
    value: 'America/Denver',
    label: 'US Mountain (Denver)',
    keywords: [
      'mountain',
      'mst',
      'mdt',
      'denver',
      'usa',
      'colorado',
      'utc-7',
      'utc-6',
    ],
  },
  {
    value: 'America/Los_Angeles',
    label: 'US Pacific (Los Angeles)',
    keywords: [
      'pacific',
      'pst',
      'pdt',
      'los angeles',
      'california',
      'usa',
      'west coast',
      'utc-8',
      'utc-7',
    ],
  },
  {
    value: 'America/Phoenix',
    label: 'US Arizona (Phoenix)',
    keywords: ['arizona', 'phoenix', 'mst', 'usa', 'utc-7'],
  },
  {
    value: 'America/Toronto',
    label: 'Canada Eastern (Toronto)',
    keywords: ['canada', 'toronto', 'eastern', 'est', 'edt', 'utc-5', 'utc-4'],
  },
  {
    value: 'America/Vancouver',
    label: 'Canada Pacific (Vancouver)',
    keywords: [
      'canada',
      'vancouver',
      'pacific',
      'pst',
      'pdt',
      'utc-8',
      'utc-7',
    ],
  },
  {
    value: 'America/Sao_Paulo',
    label: 'Brazil (São Paulo)',
    keywords: ['brazil', 'sao paulo', 'brt', 'brst', 'utc-3', 'utc-2'],
  },
  {
    value: 'Asia/Tokyo',
    label: 'Japan (Tokyo)',
    keywords: ['japan', 'tokyo', 'jst', 'utc+9'],
  },
  {
    value: 'Asia/Shanghai',
    label: 'China (Shanghai)',
    keywords: ['china', 'shanghai', 'beijing', 'cst', 'utc+8'],
  },
  {
    value: 'Asia/Hong_Kong',
    label: 'Hong Kong',
    keywords: ['hong kong', 'hkt', 'utc+8'],
  },
  {
    value: 'Asia/Singapore',
    label: 'Singapore',
    keywords: ['singapore', 'sgt', 'utc+8'],
  },
  {
    value: 'Asia/Seoul',
    label: 'South Korea (Seoul)',
    keywords: ['korea', 'seoul', 'kst', 'utc+9'],
  },
  {
    value: 'Asia/Mumbai',
    label: 'India (Mumbai)',
    keywords: ['india', 'mumbai', 'bombay', 'ist', 'utc+5:30'],
  },
  {
    value: 'Asia/Kolkata',
    label: 'India (Kolkata)',
    keywords: ['india', 'kolkata', 'calcutta', 'ist', 'utc+5:30'],
  },
  {
    value: 'Asia/Bangkok',
    label: 'Thailand (Bangkok)',
    keywords: ['thailand', 'bangkok', 'ict', 'utc+7'],
  },
  {
    value: 'Asia/Dubai',
    label: 'UAE (Dubai)',
    keywords: ['uae', 'dubai', 'emirates', 'gst', 'utc+4'],
  },
  {
    value: 'Australia/Sydney',
    label: 'Australia Eastern (Sydney)',
    keywords: ['australia', 'sydney', 'aest', 'aedt', 'utc+10', 'utc+11'],
  },
  {
    value: 'Australia/Melbourne',
    label: 'Australia Eastern (Melbourne)',
    keywords: ['australia', 'melbourne', 'aest', 'aedt', 'utc+10', 'utc+11'],
  },
  {
    value: 'Australia/Perth',
    label: 'Australia Western (Perth)',
    keywords: ['australia', 'perth', 'awst', 'utc+8'],
  },
  {
    value: 'Pacific/Auckland',
    label: 'New Zealand (Auckland)',
    keywords: ['new zealand', 'auckland', 'nzst', 'nzdt', 'utc+12', 'utc+13'],
  },
  {
    value: 'Pacific/Honolulu',
    label: 'Hawaii (Honolulu)',
    keywords: ['hawaii', 'honolulu', 'hst', 'utc-10'],
  },
]

const ScenarioCard = ({
  scenario,
  projectId,
}: {
  scenario: any
  projectId: Id<'projects'>
}) => {
  const navigate = useNavigate()
  const deleteScenario = useDeleteScenario()
  const [isDeleting, setIsDeleting] = useState(false)

  const handleDelete = async () => {
    if (
      !confirm(
        'Are you sure you want to delete this scenario? This action cannot be undone.'
      )
    ) {
      return
    }

    try {
      setIsDeleting(true)
      await deleteScenario({ id: scenario._id })
      toast.success('Scenario deleted successfully')
    } catch (error) {
      console.error('Failed to delete scenario:', error)
      toast.error('Failed to delete scenario')
    } finally {
      setIsDeleting(false)
    }
  }

  return (
    <Card className="hover:shadow-md transition-shadow cursor-pointer">
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between">
          <div
            className="flex-1"
            onClick={() =>
              navigate(`/projects/${projectId}/scenarios/${scenario._id}`)
            }
          >
            <CardTitle className="text-lg mb-2 flex items-center gap-2">
              <Database className="w-4 h-4" />
              {scenario.name}
            </CardTitle>
            <CardDescription className="text-sm">
              {scenario.description || 'No description provided'}
            </CardDescription>
          </div>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                size="sm"
                className="h-8 w-8 pointer-coarse:h-10 pointer-coarse:w-10 p-0"
              >
                <MoreHorizontal className="w-4 h-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem
                onClick={() =>
                  navigate(`/projects/${projectId}/scenarios/${scenario._id}`)
                }
              >
                Open
              </DropdownMenuItem>
              <DropdownMenuItem disabled>Edit</DropdownMenuItem>
              <DropdownMenuItem disabled>Clone</DropdownMenuItem>
              <DropdownMenuItem
                className="text-red-600"
                onClick={handleDelete}
                disabled={isDeleting}
              >
                {isDeleting ? 'Deleting...' : 'Delete'}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </CardHeader>

      <CardContent className="pt-0">
        <div className="flex items-center justify-between text-sm text-muted-foreground mb-3">
          <div className="flex items-center gap-1">
            <Calendar className="w-4 h-4" />
            <span>
              Created{' '}
              {formatDistanceToNow(new Date(scenario._creationTime), {
                addSuffix: true,
              })}
            </span>
          </div>
          {scenario.status && (
            <Badge variant="outline">{scenario.status}</Badge>
          )}
        </div>

        {scenario.optimizationObjective && (
          <div className="text-sm text-muted-foreground mb-2">
            <strong>Objective:</strong> {scenario.optimizationObjective}
          </div>
        )}

        {scenario.tags && scenario.tags.length > 0 && (
          <div className="flex flex-wrap gap-1">
            {scenario.tags.slice(0, 3).map((tag: string) => (
              <Badge key={tag} variant="outline" className="text-sm">
                {tag}
              </Badge>
            ))}
            {scenario.tags.length > 3 && (
              <Badge variant="outline" className="text-sm">
                +{scenario.tags.length - 3} more
              </Badge>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  )
}

const ProjectDetailPage = () => {
  const { projectId } = useParams<{ projectId: Id<'projects'> }>()
  const navigate = useNavigate()
  const { isMobile } = useResponsive()
  const project = useProject(projectId)
  const scenarios = useScenarios(projectId)
  const stats = useProjectStats(projectId)
  const createScenario = useCreateScenario()
  const updateProjectPartial = useMutation(api.projects.updatePartial)
  const [isCreatingScenario, setIsCreatingScenario] = useState(false)
  const [showSettings, setShowSettings] = useState(false)
  const [showAgentChat, setShowAgentChat] = useState(false)
  const [selectedTimezone, setSelectedTimezone] = useState(
    project?.timezone || 'Africa/Johannesburg'
  )
  const [isUpdatingTimezone, setIsUpdatingTimezone] = useState(false)
  const [showFullDescription, setShowFullDescription] = useState(false)

  // Sync timezone state when project data changes
  useEffect(() => {
    if (project?.timezone) {
      setSelectedTimezone(project.timezone)
    }
  }, [project?.timezone])

  const handleUpdateTimezone = async () => {
    if (!projectId || selectedTimezone === project?.timezone) return

    try {
      setIsUpdatingTimezone(true)
      await updateProjectPartial({
        id: projectId,
        timezone: selectedTimezone,
      })
      toast.success('Project timezone updated successfully')
      setShowSettings(false)
    } catch (error) {
      console.error('Failed to update timezone:', error)
      toast.error('Failed to update timezone')
    } finally {
      setIsUpdatingTimezone(false)
    }
  }

  const handleCreateScenario = async () => {
    if (!projectId) return

    try {
      setIsCreatingScenario(true)
      await createScenario({
        projectId,
        name: `New Scenario ${Date.now()}`,
        description: 'A new optimization scenario',
        optimizationObjective: 'minimize_total_cost',
      })
      toast.success('Scenario created successfully')
    } catch (error) {
      console.error('Failed to create scenario:', error)
      toast.error('Failed to create scenario')
    } finally {
      setIsCreatingScenario(false)
    }
  }

  if (!projectId) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-center">
          <h1 className="text-xl font-semibold mb-2">Project Not Found</h1>
          <p className="text-muted-foreground mb-4">
            The requested project could not be found.
          </p>
          <Button onClick={() => navigate('/projects')}>
            Back to Projects
          </Button>
        </div>
      </div>
    )
  }

  if (project === undefined || scenarios === undefined) {
    return (
      <div className="@container flex flex-col h-full bg-background">
        {/* Header Skeleton */}
        <div className="flex flex-col @lg:flex-row @lg:items-center justify-between p-4 @lg:p-6 border-b border-border gap-4">
          <div className="flex-1 space-y-3">
            <div className="flex items-center gap-4">
              <div className="h-8 w-24 bg-muted animate-pulse rounded" />
              <div className="h-6 w-48 bg-muted animate-pulse rounded" />
            </div>
            <div className="h-4 w-96 bg-muted animate-pulse rounded" />
            <div className="flex gap-4">
              <div className="h-4 w-32 bg-muted animate-pulse rounded" />
              <div className="h-4 w-32 bg-muted animate-pulse rounded" />
              <div className="h-4 w-32 bg-muted animate-pulse rounded" />
            </div>
          </div>
        </div>
        {/* Content Skeleton */}
        <div className="flex-1 p-4 @lg:p-6">
          <div className="grid grid-cols-1 @md:grid-cols-2 @lg:grid-cols-3 gap-4 @lg:gap-6">
            {Array.from({ length: 3 }).map((_, i) => (
              <SkeletonCard key={i} hasStats={false} />
            ))}
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="@container flex flex-col h-full bg-background">
      {/* Header */}
      <div className="flex flex-col @lg:flex-row @lg:items-center justify-between p-4 @lg:p-6 border-b border-border gap-4">
        <div className="flex-1">
          <div className="flex items-center gap-4 mb-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate('/projects')}
              className={cn(
                'text-muted-foreground hover:text-foreground',
                'h-8 pointer-coarse:h-10'
              )}
            >
              ← Projects
            </Button>
            <h1 className="text-lg @lg:text-xl font-semibold text-foreground">
              {project.name}
            </h1>
          </div>

          {/* Description with mobile truncation */}
          <div className="mb-3">
            <p
              className={cn(
                'text-sm text-muted-foreground',
                isMobile &&
                  !showFullDescription &&
                  (project.description?.length || 0) > 150 &&
                  'line-clamp-3'
              )}
            >
              {project.description || 'No description provided'}
            </p>
            {isMobile && (project.description?.length || 0) > 150 && (
              <Button
                variant="link"
                onClick={() => setShowFullDescription(!showFullDescription)}
                className="p-0 h-auto mt-2 text-sm"
              >
                {showFullDescription ? 'Show less' : 'Read more'}
              </Button>
            )}
          </div>

          {/* Project Stats - Responsive badges */}
          {stats && (
            <div className="flex flex-wrap items-center gap-4">
              <div className="flex items-center gap-1 text-sm text-muted-foreground">
                <Database className="w-4 h-4" />
                <span>
                  {stats.scenarioCount} scenario
                  {stats.scenarioCount !== 1 ? 's' : ''}
                </span>
              </div>
              <div className="flex items-center gap-1 text-sm text-muted-foreground">
                <Users className="w-4 h-4" />
                <span>
                  {stats.vehicleCount} vehicle
                  {stats.vehicleCount !== 1 ? 's' : ''}
                </span>
              </div>
              <div className="flex items-center gap-1 text-sm text-muted-foreground">
                <Calendar className="w-4 h-4" />
                <span>
                  {stats.jobCount} job{stats.jobCount !== 1 ? 's' : ''}
                </span>
              </div>
            </div>
          )}
        </div>

        <div className="flex flex-col @lg:flex-row items-stretch @lg:items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowAgentChat(true)}
            className="h-8 pointer-coarse:h-12 justify-start @lg:justify-center"
          >
            <Bot className="w-4 h-4 mr-2" />
            <span className="hidden sm:inline">AI Assistant</span>
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => navigate(`/projects/${projectId}/locations`)}
            className="h-8 pointer-coarse:h-12 justify-start @lg:justify-center"
          >
            <MapPin className="w-4 h-4 mr-2" />
            Master Locations
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowSettings(true)}
            className="h-8 pointer-coarse:h-12 justify-start @lg:justify-center"
          >
            <Settings className="w-4 h-4 mr-2" />
            Settings
          </Button>
          <Button
            size="sm"
            onClick={handleCreateScenario}
            disabled={isCreatingScenario}
            className="h-8 pointer-coarse:h-12 justify-start @lg:justify-center"
          >
            {isCreatingScenario ? (
              <>
                <LoadingSpinner className="w-4 h-4 mr-2" />
                Creating...
              </>
            ) : (
              <>
                <Plus className="w-4 h-4 mr-2" />
                New Scenario
              </>
            )}
          </Button>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 p-6">
        {scenarios.length === 0 ? (
          <div className="text-center py-12">
            <div className="w-16 h-16 bg-muted rounded-full flex items-center justify-center mx-auto mb-4">
              <Database className="w-8 h-8 text-muted-foreground" />
            </div>
            <h3 className="text-lg font-semibold text-foreground mb-2">
              Create your first scenario
            </h3>
            <p className="text-muted-foreground mb-6 max-w-md mx-auto">
              Scenarios help you test different optimization parameters and
              compare results. Start by creating your first scenario.
            </p>
            <Button
              onClick={handleCreateScenario}
              disabled={isCreatingScenario}
            >
              {isCreatingScenario ? (
                <>
                  <LoadingSpinner className="w-4 h-4 mr-2" />
                  Creating...
                </>
              ) : (
                <>
                  <Plus className="w-4 h-4 mr-2" />
                  Create Scenario
                </>
              )}
            </Button>
          </div>
        ) : (
          <>
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="text-xl font-semibold text-foreground">
                  Scenarios
                </h2>
                <p className="text-sm text-muted-foreground">
                  Manage optimization scenarios for this project (
                  {scenarios.length} scenario{scenarios.length !== 1 ? 's' : ''}
                  )
                </p>
              </div>
            </div>

            <div className="grid grid-cols-1 @md:grid-cols-2 @lg:grid-cols-3 gap-4 @lg:gap-6">
              {scenarios.map((scenario: any) => (
                <ScenarioCard
                  key={scenario._id}
                  scenario={scenario}
                  projectId={projectId}
                />
              ))}
            </div>
          </>
        )}
      </div>

      {/* Agent Chat Panel */}
      {showAgentChat && (
        <AgentChatPanel
          projectId={projectId}
          onClose={() => setShowAgentChat(false)}
        />
      )}

      {/* Settings Dialog */}
      <Dialog open={showSettings} onOpenChange={setShowSettings}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Project Settings</DialogTitle>
            <DialogDescription>
              Configure project-level settings including timezone and
              preferences.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="timezone">Project Timezone</Label>
              <SearchableSelect
                value={selectedTimezone}
                onValueChange={setSelectedTimezone}
                options={TIMEZONE_OPTIONS}
                placeholder="Search and select timezone..."
                searchPlaceholder="Search timezones (e.g. 'EST', 'Pacific', 'London')..."
                emptyMessage="No timezones found"
                maxHeight={300}
                className="w-full"
              />
              <p className="text-sm text-muted-foreground">
                This timezone will be used for all time-based operations in this
                project.
              </p>
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setSelectedTimezone(project?.timezone || 'Africa/Johannesburg')
                setShowSettings(false)
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleUpdateTimezone}
              disabled={
                isUpdatingTimezone || selectedTimezone === project?.timezone
              }
            >
              {isUpdatingTimezone ? (
                <>
                  <LoadingSpinner className="w-4 h-4 mr-2" />
                  Updating...
                </>
              ) : (
                'Save Changes'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}

export default ProjectDetailPage
